import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  StarIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  HeartIcon,
  WifiIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { facilitiesAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

// Types
interface Facility {
  _id: string;
  name: string;
  licenseNumber: string;
  type: string;
  capacity: number;
  currentOccupancy: number;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  contact: {
    name: string;
    phone: string;
    email: string;
  };
  amenities: string[];
  costRange: string;
  minCost: number;
  maxCost: number;
  acceptsInsurance: boolean;
  specialties: string[];
  notes: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const FacilityList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  const { success: showSuccess, error: showError } = useToast();

  // Fetch facilities with filters
  const {
    data: facilitiesData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['facilities', searchTerm, typeFilter, availabilityFilter, priceRange, sortBy, sortOrder, currentPage],
    queryFn: () => facilitiesAPI.getFacilities({
      search: searchTerm,
      type: typeFilter !== 'all' ? typeFilter : undefined,
      availability: availabilityFilter !== 'all' ? availabilityFilter : undefined,
      minPrice: priceRange.min ? parseInt(priceRange.min) : undefined,
      maxPrice: priceRange.max ? parseInt(priceRange.max) : undefined,
      sort: `${sortOrder === 'desc' ? '-' : ''}${sortBy}`,
      page: currentPage,
      limit: itemsPerPage
    }),
    placeholderData: (previousData) => previousData,
  });

  // Filter facilities locally for immediate response
  const getFilteredFacilities = () => {
    if (!(facilitiesData as any)?.data?.data?.facilities) return [];
    
    return (facilitiesData as any).data.data.facilities.filter((facility: Facility) => {
      const matchesSearch = searchTerm === '' ||
        facility.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${facility.address.city} ${facility.address.state}`.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = typeFilter === 'all' || facility.type === typeFilter;
      
      const matchesAvailability = availabilityFilter === 'all' ||
        (availabilityFilter === 'available' && (facility.capacity - facility.currentOccupancy) > 0) ||
        (availabilityFilter === 'waitlist' && (facility.capacity - facility.currentOccupancy) === 0);
      
      const matchesPrice = (!priceRange.min || facility.minCost >= parseInt(priceRange.min)) &&
        (!priceRange.max || facility.maxCost <= parseInt(priceRange.max));
      
      return matchesSearch && matchesType && matchesAvailability && matchesPrice;
    });
  };

  const filteredFacilities = getFilteredFacilities();
  const totalPages = Math.ceil(((facilitiesData as any)?.data?.data?.pagination?.totalFacilities || 0) / itemsPerPage);

  // Helper functions
  const getTypeBadge = (type: string) => {
    const typeConfig = {
      'Assisted Living': { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Assisted Living' },
      'Nursing Home': { color: 'bg-red-100 text-red-800 border-red-200', label: 'Nursing Home' },
      'Memory Care': { color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'Memory Care' },
      'Independent Living': { color: 'bg-green-100 text-green-800 border-green-200', label: 'Independent Living' },
      'Skilled Nursing': { color: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Skilled Nursing' },
      'Rehabilitation Center': { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', label: 'Rehabilitation Center' },
      'Adult Day Care': { color: 'bg-pink-100 text-pink-800 border-pink-200', label: 'Adult Day Care' },
      'Home Care': { color: 'bg-teal-100 text-teal-800 border-teal-200', label: 'Home Care' },
      'Other': { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Other' }
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig['Other'];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getAvailabilityBadge = (facility: Facility) => {
    const available = facility.capacity - facility.currentOccupancy;
    if (available > 0) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded bg-green-50 text-green-700">
          {available} Available
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded bg-red-50 text-red-700">
          Wait List
        </span>
      );
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getAmenityIcon = (amenity: string) => {
    const lowerAmenity = amenity.toLowerCase();
    if (lowerAmenity.includes('wifi') || lowerAmenity.includes('internet')) return WifiIcon;
    if (lowerAmenity.includes('transport') || lowerAmenity.includes('shuttle')) return TruckIcon;
    if (lowerAmenity.includes('medical') || lowerAmenity.includes('health')) return HeartIcon;
    return StarIcon;
  };

  const exportFacilities = () => {
    showSuccess('Export functionality will be implemented in the next phase');
  };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load facilities. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-left">🏢 Facilities</h1>
          <p className="text-gray-600">
            Browse and manage senior care facilities
          </p>
        </div>
        <div className="flex gap-3 mt-4 sm:mt-0">
          <button
            onClick={exportFacilities}
            className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export
          </button>
          <Link
            to="/facilities/new"
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Facility
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search facilities by name or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              <option value="independent-living">Independent Living</option>
              <option value="assisted-living">Assisted Living</option>
              <option value="memory-care">Memory Care</option>
              <option value="skilled-nursing">Skilled Nursing</option>
              <option value="continuing-care">Continuing Care</option>
            </select>
          </div>

          {/* Availability Filter */}
          <div>
            <select
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Availability</option>
              <option value="available">Available Now</option>
              <option value="waitlist">Wait List Only</option>
            </select>
          </div>

          {/* Price Range */}
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Min $"
              value={priceRange.min}
              onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <input
              type="number"
              placeholder="Max $"
              value={priceRange.max}
              onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-200">
          <span className="text-sm text-gray-600">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="updatedAt">Last Updated</option>
            <option value="name">Facility Name</option>
            <option value="minCost">Price</option>
            <option value="rating">Rating</option>
            <option value="capacity.available">Availability</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
          >
            {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          Showing {filteredFacilities.length} of {(facilitiesData as any)?.data?.data?.pagination?.totalFacilities || 0} facilities
        </p>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm bg-primary-100 text-primary-800 rounded">
              {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Facility Grid */}
      {filteredFacilities.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <BuildingOfficeIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No facilities found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || typeFilter !== 'all' || availabilityFilter !== 'all'
              ? 'Try adjusting your search criteria or filters'
              : 'Get started by adding your first facility'}
          </p>
          <Link
            to="/facilities/new"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add First Facility
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFacilities.map((facility: Facility) => (
            <div
              key={facility._id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200"
            >
              {/* Facility Card Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">
                      {facility.name}
                    </h3>
                    <div className="flex items-center gap-2 mb-2">
                      {getTypeBadge(facility.type)}
                      {getAvailabilityBadge(facility)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Facility Details */}
              <div className="p-4 space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="truncate">
                    {facility.address.city}, {facility.address.state}
                  </span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <UserGroupIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span>
                    {facility.capacity} units ({facility.capacity - facility.currentOccupancy} available)
                  </span>
                </div>

                <div className="flex items-center text-sm text-gray-600">
                  <CurrencyDollarIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span>
                    {facility.costRange}
                  </span>
                </div>

                {/* Amenities Preview */}
                {facility.amenities.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-500 font-medium">Featured Amenities:</div>
                    <div className="flex flex-wrap gap-1">
                      {facility.amenities.slice(0, 3).map((amenity, index) => {
                        const IconComponent = getAmenityIcon(amenity);
                        return (
                          <div key={index} className="flex items-center bg-gray-50 px-2 py-1 rounded text-xs text-gray-600">
                            <IconComponent className="w-3 h-3 mr-1" />
                            <span>{amenity}</span>
                          </div>
                        );
                      })}
                      {facility.amenities.length > 3 && (
                        <span className="text-xs text-gray-500 px-2 py-1">
                          +{facility.amenities.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes Preview */}
                {facility.notes && (
                  <div className="text-xs text-gray-500">
                    {facility.notes.length > 100
                      ? `${facility.notes.substring(0, 100)}...`
                      : facility.notes
                    }
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="p-4 pt-0">
                <div className="flex gap-2">
                  <Link
                    to={`/facilities/${facility._id}`}
                    className="flex-1 flex items-center justify-center px-3 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
                  >
                    <EyeIcon className="w-4 h-4 mr-1" />
                    View
                  </Link>
                  <Link
                    to={`/facilities/${facility._id}/edit`}
                    className="flex-1 flex items-center justify-center px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4 mr-1" />
                    Edit
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FacilityList;
